import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.success) {
        localStorage.setItem('am_admin_token', response.data.token);
        localStorage.setItem('am_admin_user', JSON.stringify(response.data.user));
        navigate('/admin');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-900 via-amber-800 to-amber-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_build-showcase-148/artifacts/4oqualfy_temp_image_B69CD62C-A65E-43CF-A04E-C52C20BE3294.WEBP"
            alt="AM Carpentry Logo"
            className="h-20 w-20 object-contain mx-auto mb-4 bg-white rounded-full p-2"
            data-testid="admin-login-logo"
          />
          <h1 className="text-3xl font-bold text-white">AM Carpentry</h1>
          <p className="text-amber-200 mt-2">Admin Dashboard</p>
        </div>

        <Card className="border-2 border-amber-700 shadow-2xl">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-amber-950 mb-6 text-center">Sign In</h2>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
              <div>
                <label className="block text-sm font-medium text-amber-900 mb-2">
                  <Mail className="inline w-4 h-4 mr-1" />
                  Email
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@example.com"
                  className="border-amber-300 focus:border-amber-600"
                  data-testid="admin-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-amber-900 mb-2">
                  <Lock className="inline w-4 h-4 mr-1" />
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter password"
                    className="border-amber-300 focus:border-amber-600 pr-10"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-amber-700 hover:text-amber-900"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md" data-testid="admin-login-error">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-amber-800 hover:bg-amber-900 text-white py-6 text-lg disabled:opacity-50"
                data-testid="admin-login-submit"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <a href="/" className="text-sm text-amber-700 hover:text-amber-900 transition-colors">
                ← Back to Website
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
